import { useEffect, useState } from "react";
import { Select } from "../components/ui/inputs/Select";
import { useTimeStore } from "../stores/time";
import { getTimesForUser, getUsers } from "../api/users";
import { useUserContext } from "../contexts/userContext";
import DatePicker from "../components/time/DatePicker";
import WeekCalendar from "../components/time/WeekCalendar";
import TableTime from "../components/ui/tables/TableTime";
import { getTimesUnvalidated } from "../api/times";
import ValidateTime from "../components/time/ValidateTime";
import { useUtilsStore } from "../stores/components/utils";
import "react-datepicker/dist/react-datepicker.css";
import DatePickerReact from "react-datepicker";
import { convertDecimalToHour } from "../services/date";
import Totals from "../components/time/Totals";
import { showToastError } from "../services/toast";
import { useLoaderStore } from "../stores/components/loader";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import useModals from "../hooks/useModals";
import Holidays from "../components/time/Holidays";
import TableHolidays from "../components/ui/tables/TableHolidays";
import {
  getHolidayRequests,
  getHolidayRequestsValidatedNotSent,
} from "../api/events";
import RefuseHolidays from "../components/time/RefuseHolidays";
import {
  ActionButton,
  PageHeader,
  PagePanel,
  PageShell,
  PanelCenter,
  PanelToolbar,
  SearchField,
  StatChip,
  ToolbarRow,
} from "../components/ui/page/PageShell";

const TITLE_BY_TAB: Record<string, string> = {
  Time: "Calendrier",
  TimeToValidate: "Temps à valider",
  HolidaysToValidate: "Congés et absences à valider",
};

const Time = () => {
  // Context
  const { idUser, role, rights, isAuthenticated } = useUserContext();
  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();

  // Store
  const {
    users,
    setUsers,
    idUserSelected,
    setIdUserSelected,
    weekDays,
    setTimes,
    update,
    tab,
    setTab,
    timesToValidate,
    setTimesToValidate,
    dataFilter,
    setDataFilter,
    requestsHolidays,
    setRequestsHolidays,
  } = useTimeStore();
  const { searchTerm, setSearchTerm } = useUtilsStore();
  const { isLoading, setIsLoading } = useLoaderStore();

  // State
  const [timeMax, setTimeMax] = useState(0);
  const [timeEntered, setTimeEntered] = useState<string>("00:00");
  const [selectedDate, setselectedDate] = useState<any>(new Date());

  // Permet de changer l'utilisateur sélectionné
  const handleSelectChange = (e: any) => {
    const selectedUserId = e.target.value;
    setIdUserSelected(selectedUserId);
    const selectedUser = users.find(
      (user) => user.value === parseInt(selectedUserId, 10),
    );

    setTimeMax(selectedUser?.hours || 0);
  };

  useEffect(() => {
    // Ne pas faire d'appels API si l'utilisateur n'est pas authentifié
    if (!isAuthenticated) {
      return;
    }

    // Récupère les utilisateurs
    setIsLoading(true);
    getUsers(true)
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.firstName + " " + data.lastName,
          value: data.id,
          hours: data.hours,
        }));

        if (idUser) {
          setIdUserSelected(idUser);
          setTimeMax(
            formattedData.find((user: any) => user.value === idUser)?.hours,
          );
        }
        setUsers(formattedData);
        setIsLoading(false);
      })
      .catch((err) => {
        showToastError(err.response.data.message);
        setIsLoading(false);
      });
  }, [isAuthenticated]);

  useEffect(() => {
    // Ne pas faire d'appels API si l'utilisateur n'est pas authentifié
    if (!isAuthenticated) {
      return;
    }

    // Récupère les temps pour l'utilisateur sélectionné
    if (tab === "Time" && idUserSelected !== 0) {
      setIsLoading(true);
      getTimesForUser(idUserSelected, weekDays[0].toISOString())
        .then((res) => {
          setTimes(res.data);
          const total = res?.data
            .filter((time: any) => time.timesLabel !== "Absences")
            .reduce(
              (acc: number, time: any) => acc + Number(time.lengthDecimal),
              0,
            );
          setTimeEntered(convertDecimalToHour(total));
          setIsLoading(false);
        })
        .catch((err) => {
          showToastError(err.response.data.message);
          setIsLoading(false);
        });
    } else if (tab === "TimeToValidate") {
      // Récupère les temps à valider
      setIsLoading(true);
      getTimesUnvalidated()
        .then((res) => {
          if (res.data.length > 0) {
            setTimesToValidate(res.data);
          } else {
            setTimesToValidate([]);
          }
          setIsLoading(false);
        })
        .catch((err) => {
          showToastError(err.response.data.message);
          setIsLoading(false);
        });
    } else if (tab === "HolidaysToValidate") {
      // Récupère les demandes de congés / abscences
      setIsLoading(true);
      (rights.includes(1004) /*Si cest Ute*/
        ? getHolidayRequestsValidatedNotSent()
        : getHolidayRequests()
      )
        .then((res) => {
          if (res.data.length > 0) {
            setRequestsHolidays(res.data);
          } else {
            setRequestsHolidays([]);
          }
          setIsLoading(false);
        })
        .catch((err) => {
          showToastError(err.response.data.message);
          setIsLoading(false);
        });
    }
  }, [idUserSelected, weekDays, update, tab, selectedDate, isAuthenticated]);

  // Gestion de la recherche
  useEffect(() => {
    let data: any = [];

    if (tab === "TimeToValidate") {
      data = [...timesToValidate];
    }

    // Filtrage combiné par recherche texte ET par date
    if (tab === "TimeToValidate") {
      data = data.filter((item: any) => {
        // Filtrage par date (si selectedDate est défini)
        let dateMatch = true;
        if (selectedDate) {
          const [day, month, year] = item["date"].split("/"); // Découpage de "24/03/2025" → ["24", "03", "2025"]
          const itemDate = new Date(`${year}-${month}-${day}`); // Création d'une date valide en "YYYY-MM-DD"
          dateMatch =
            itemDate.getMonth() === selectedDate.getMonth() &&
            itemDate.getFullYear() === selectedDate.getFullYear();
        }

        // Filtrage par recherche texte (si searchTerm n'est pas vide)
        let textMatch = true;
        if (searchTerm !== "") {
          textMatch =
            item["customerFile"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["mission"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item["user"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item["date"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item["timesLabel"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase());
        }

        // L'élément doit correspondre aux deux critères
        return dateMatch && textMatch;
      });
    } else if (tab === "HolidaysToValidate") {
      data = [...requestsHolidays];
      if (searchTerm !== "") {
        data = data.filter((item: any) => {
          return (
            item["nameTimeLabel"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["user"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item["startDate"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["endDate"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item["description"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
          );
        });
      } else {
        data = [...requestsHolidays];
      }
    }

    setDataFilter(data);
  }, [
    searchTerm,
    timesToValidate,
    requestsHolidays,
    selectedDate,
    update,
    tab,
  ]);

  const isCalendar = tab === "Time";

  return (
    <PageShell>
      <PageHeader
        eyebrow="Temps"
        title={TITLE_BY_TAB[tab] ?? "Temps"}
        actions={
          <>
            {role !== "Lecteur" && isCalendar && (
              <Select
                label=""
                name="users"
                value={idUserSelected.toString()}
                onChange={handleSelectChange}
                fetchOptions={users}
              />
            )}

            {(role === "Admin" ||
              role === "Direction" ||
              role === "Editeur" ||
              rights.includes(1004)) &&
            (tab === "TimeToValidate" || tab === "HolidaysToValidate") ? (
              <ValidateTime />
            ) : null}

            {rights.includes(1004) && tab === "HolidaysToValidate" ? (
              <RefuseHolidays />
            ) : null}

            {role !== "Lecteur" &&
              role !== "Editeur" &&
              role !== "Superviseur" && (
                <ActionButton
                  variant="primary"
                  onClick={() =>
                    setTab(isCalendar ? "TimeToValidate" : "Time")
                  }
                >
                  {isCalendar ? "Valider les temps" : "Calendrier"}
                </ActionButton>
              )}

            {role === "Editeur" && tab === "HolidaysToValidate" ? (
              <ActionButton variant="primary" onClick={() => setTab("Time")}>
                Calendrier
              </ActionButton>
            ) : null}
          </>
        }
      />

      <PagePanel>
        {/* Section recherche / filtre */}
        <PanelToolbar>
          <ToolbarRow>
            <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
              {tab === "TimeToValidate" || tab === "HolidaysToValidate" ? (
                <SearchField
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Rechercher un dossier, un utilisateur…"
                />
              ) : null}

              {tab === "TimeToValidate" ? (
                <DatePickerReact
                  selected={selectedDate}
                  onChange={(date: any) => setselectedDate(date)}
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  portalId="datepicker-portal"
                  wrapperClassName="relative z-20"
                  popperClassName="!z-[100]"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 font-Nunito text-sm text-slate-800 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 focus:outline-none sm:w-40"
                />
              ) : isCalendar ? (
                <div className="flex flex-wrap items-center gap-2">
                  <DatePicker />
                  <ActionButton
                    onClick={() => {
                      openModal(
                        "holidays",
                        <Holidays
                          closeModal={() => closeModal("holidays")}
                          idEvent={null}
                        />,
                      );
                    }}
                  >
                    Demande d'absence
                  </ActionButton>

                  {role === "Admin" ||
                  role === "Direction" ||
                  role === "Editeur" ? (
                    <ActionButton onClick={() => setTab("HolidaysToValidate")}>
                      Validation absence
                    </ActionButton>
                  ) : null}
                </div>
              ) : null}
            </div>

            {isCalendar && (
              <div className="flex flex-wrap items-center gap-2">
                <StatChip label="Potentiel" value={`${timeMax} h`} />
                <StatChip
                  label="Saisi"
                  value={`${timeEntered} h`}
                  tone="indigo"
                />
              </div>
            )}
          </ToolbarRow>
        </PanelToolbar>

        {/* Contenu principal */}
        {isLoading ? (
          <PanelCenter>
            <LoadingSpinner size="large" text="Chargement des données..." />
          </PanelCenter>
        ) : tab === "HolidaysToValidate" ? (
          <TableHolidays
            columns={[
              { header: "Demande", accessor: "nameTimeLabel" },
              { header: "Utilisateur", accessor: "user" },
              { header: "Date de début", accessor: "startDate" },
              { header: "Heure de début", accessor: "startHour" },
              { header: "Date de fin", accessor: "endDate" },
              { header: "Heure de fin", accessor: "endHour" },
              { header: "Commentaire", accessor: "description" },
            ]}
            data={dataFilter}
          />
        ) : tab === "TimeToValidate" ? (
          <>
            <TableTime
              columns={[
                { header: "Dossier", accessor: "customerFile" },
                { header: "Mission", accessor: "mission" },
                { header: "Utilisateur", accessor: "user" },
                { header: "Date", accessor: "date" },
                { header: "Temps", accessor: "length" },
              ]}
              data={dataFilter}
            />
            <Totals data={dataFilter} />
          </>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto p-3 sm:p-4">
            <WeekCalendar />
          </div>
        )}
      </PagePanel>

      {/* Conteneur des modales (séparé du calendrier) */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}
    </PageShell>
  );
};

export default Time;
